import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { nodeAtomFamily } from "./nodeAtomFamily";
import { isDraftNode } from "../utils/isDraftNode";
import { isTemplateNode } from "../utils/isTemplateNode";
import { determineAssetType, getAssetId } from "../utils";
import type { Node } from "@xyflow/react";
import { DefaultNodeData, NodeType } from "../types";

/**
 * Atom family that determines if a node is a draft or template node
 * based on its asset properties. Handles the full pipeline of:
 * 1. Getting the node via nodeAtomFamily
 * 2. Extracting asset type and ID
 * 3. Checking draft/template status
 *
 * Usage:
 * ```ts
 * const isDraftOrTemplateNode = useAtomValue(isDraftOrTemplateNodeAtomFamily(parentId));
 * ```
 */
export const isDraftOrTemplateNodeAtomFamily = atomFamily(
  (nodeId: string | undefined | null) => {
    if (!nodeId) {
      return atom(false);
    }

    const baseAtom = atom((get) => {
      const node = get(nodeAtomFamily(nodeId)) as Node | undefined;

      if (!node) {
        return false;
      }

      // Template nodes (starting with "_TEMPLATE_") are included
      if (isTemplateNode(nodeId)) {
        return true;
      }

      // Check if it's a draft node based on asset properties
      const assetType = determineAssetType(
        (node.data as DefaultNodeData)?.services,
        node.type as NodeType,
      );

      const assetId = getAssetId(node.data as any, assetType);

      return isDraftNode({ assetId, assetType });
    });

    baseAtom.debugLabel = `isDraftOrTemplateNodeAtomFamily(${nodeId})`;
    return baseAtom;
  },
);
